import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";
import MaltoraLogo from "../components/layout/MaltoraLogo";
import { cn } from "../lib/utils";

const BENEFITS = [
  {
    title: "Up in minutes",
    desc: "Onboard your team and start taking orders the same day.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
  {
    title: "Every stakeholder",
    desc: "Portals for buyers, sellers, and reps — all connected.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    title: "No hidden fees",
    desc: "Simple pricing that scales with your business.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
  },
  {
    title: "Secure by default",
    desc: "Role-based access so everyone sees only what they need.",
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
];

const ROLES: Array<{ value: string; label: string }> = [
  { value: "brewery", label: "Brewery / Manufacturer" },
  { value: "distributor", label: "Distributor" },
  { value: "retailer", label: "Retailer / Outlet" },
  { value: "sales_rep", label: "Sales Representative" },
  { value: "other", label: "Other" },
];

export default function RegisterPage() {
  const [showPwd, setShowPwd] = useState(false);

  return (
    <Layout chrome={false}>
      <div className="signin-page">
        <div className="signin-left">
          <Link to="/" className="signin-logo" aria-label="Maltora home">
            <MaltoraLogo />
          </Link>

          <div className="signin-left-body">
            <h2 className="signin-left-heading">
              Join the beverage
              <br />
              businesses running on <em>Maltora.</em>
            </h2>

            <div className="signin-benefits">
              {BENEFITS.map((b) => (
                <div className="signin-benefit" key={b.title}>
                  <div className="signin-benefit-icon">{b.icon}</div>
                  <div className="signin-benefit-title">{b.title}</div>
                  <div className="signin-benefit-desc">{b.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="signin-right">
          <div className="signin-form-wrap">
            <h1 className="signin-form-heading">Create your account</h1>
            <p className="signin-form-sub">
              Get started with Maltora — free for 14 days
            </p>

            <form
              action="#"
              method="post"
              noValidate
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label" htmlFor="first-name">
                    First name
                  </label>
                  <div className="form-input-wrap">
                    <input
                      className="form-input form-input-no-icon"
                      id="first-name"
                      type="text"
                      name="first_name"
                      placeholder="Arjun"
                      autoComplete="given-name"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="last-name">
                    Last name
                  </label>
                  <div className="form-input-wrap">
                    <input
                      className="form-input form-input-no-icon"
                      id="last-name"
                      type="text"
                      name="last_name"
                      placeholder="Sharma"
                      autoComplete="family-name"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="company">
                  Company name
                </label>
                <div className="form-input-wrap">
                  <span className="form-input-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  </span>
                  <input
                    className="form-input"
                    id="company"
                    type="text"
                    name="company"
                    placeholder="Horizon Distributors Pvt. Ltd."
                    autoComplete="organization"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="role">
                  Your role
                </label>
                <select
                  className="form-select"
                  id="role"
                  name="role"
                  required
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select your role
                  </option>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  Work email
                </label>
                <div className="form-input-wrap">
                  <span className="form-input-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <input
                    className="form-input"
                    id="email"
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">
                  Password
                </label>
                <div className="form-input-wrap">
                  <span className="form-input-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    className="form-input"
                    id="password"
                    type={showPwd ? "text" : "password"}
                    name="password"
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className={cn(
                      "form-input-toggle",
                      showPwd && "form-input-toggle-active"
                    )}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    onClick={() => setShowPwd((v) => !v)}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="form-terms">
                <input
                  className="form-checkbox"
                  type="checkbox"
                  id="terms"
                  name="terms"
                  required
                />
                <label className="form-terms-text" htmlFor="terms">
                  I agree to Maltora&apos;s <a href="#">Terms of Service</a> and{" "}
                  <a href="#">Privacy Policy</a>
                </label>
              </div>

              <button type="submit" className="btn-signin">
                Create Account
              </button>
            </form>

            <p className="signin-signup-row">
              Already have an account?{" "}
              <Link to="/signin" state={{ fromApp: true }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
